import React from "react";
import Utilities from "@util/Utilities";
import AlertUtilities from "@app/site/secured/alert/util/AlertUtilities";
import Tag from "@app/component/tag/Tag";
import AlertExecution from "@app/model/alert/AlertExecution";
import PriceComparisonAlertExecutionNotificationOutput
    from "@app/model/alert/output/PriceComparisonAlertExecutionNotificationOutput";
import PriceComparisonAlertRulePricePoint from "@app/model/alert/PriceComparisonAlertRulePricePoint";


const PriceComparisonAlertExecutionNotificationRowFragment: React.FunctionComponent<{
    alertExecution: AlertExecution,
    notificationOutput: PriceComparisonAlertExecutionNotificationOutput,
    marketStateDetailsModal: any,
    showPreviousRun?: boolean,
    isInDrawer?: boolean
}>
    = ({alertExecution, notificationOutput, marketStateDetailsModal, showPreviousRun = false, isInDrawer = false}) => {

    const findCompetitor = (competitorId: number) => {
        return alertExecution.alertOutput.competitors!.find(c => c.id === competitorId)
    }

    const createPriceChangeIndicator = (currentAmount: number, previousAmount: number) => {
        if(previousAmount === currentAmount) return <span/>
        return currentAmount > previousAmount ? <span><span className="text-green-500">▲</span><span className="text-gray-400 text-xs">({currentAmount - previousAmount})</span></span>
            : <span><span className="text-red-500">▼</span><span className="text-gray-400 text-xs">({previousAmount - currentAmount})</span></span>
    }

    const showPriceChangeIndicator = (output: PriceComparisonAlertExecutionNotificationOutput) => {
        return output.previous
            ? createPriceChangeIndicator(output.current.price.amount, output.previous.price.amount)
            : <div/>
    }

    const showCompetitorsPriceChangeIndicator = (output: PriceComparisonAlertExecutionNotificationOutput) => {
        return output.previous
            ? createPriceChangeIndicator(output.current.competitorsPrice.amount, output.previous.competitorsPrice.amount)
            : <div/>
    }

    return <tr>
        <td className="whitespace-nowrap py-3 pl-4 pr-3 sm:pl-0 text-sm font-medium text-gray-900">
            {Utilities.formatDateString(notificationOutput.current.statDate, "ddd DD MMM")}
            {!showPreviousRun && notificationOutput.isNew &&
                <span className="text-xs text-blue-600 ml-1">• New</span>}
            {!showPreviousRun && notificationOutput.isUpdated &&
                <span className="text-xs text-blue-600 ml-1">• Updated</span>}
        </td>
        <td className="whitespace-nowrap px-2 py-3 text-sm">
            {Utilities.formatDouble(notificationOutput.current.price.amount, 0)}
            {showPreviousRun && showPriceChangeIndicator(notificationOutput)}
        </td>
        <td className="whitespace-nowrap px-2 py-3 text-sm">
            {Utilities.formatDouble(notificationOutput.current.competitorsPrice.amount, 0)}
            {showPreviousRun && showCompetitorsPriceChangeIndicator(notificationOutput)}
        </td>
        {alertExecution.alertOutput.priceComparisonAlertRule.pricePoint !== PriceComparisonAlertRulePricePoint.MEDIAN &&
            <td className="whitespace-nowrap px-2 py-3 text-sm">
                {findCompetitor(notificationOutput.current.competitorId)?.name}
            </td>}
        {!showPreviousRun && <td className="whitespace-nowrap px-2 py-3 text-sm">
            <FormatDifferencePercentage percentage={AlertUtilities.calculateDifference(notificationOutput.current)}/>
        </td>}
        {showPreviousRun && !!notificationOutput.previous && <td className="whitespace-nowrap px-2 py-3 text-sm">
            <FormatDifferencePercentage percentage={notificationOutput.previousRate * 100}/>
            <span className="ml-1 mr-1">→</span>
            <FormatDifferencePercentage percentage={notificationOutput.currentRate * 100}/>
        </td>}
        {showPreviousRun && !notificationOutput.previous && <td className="whitespace-nowrap px-2 py-3 text-sm">
            <FormatDifferencePercentage percentage={AlertUtilities.calculateDifference(notificationOutput.current)}/>
        </td>}
        {!showPreviousRun && !isInDrawer &&
            <td className="relative whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                <a href="#"
                   className="text-blue-600 font-semibold hover:text-blue-900"
                   onClick={() => marketStateDetailsModal.current!.open(notificationOutput!.current.alertId, notificationOutput.current.marketStateId)}>Open</a>
            </td>}
    </tr>
}

const FormatDifferencePercentage: React.FunctionComponent<{ percentage: number }> = ({percentage}) => {
    const rounded = parseInt(Utilities.formatDouble(percentage, 0));
    return rounded > 0 ? <Tag state='critical'>+ {rounded} %</Tag> : <Tag state="success">{rounded} %</Tag>
}

export default PriceComparisonAlertExecutionNotificationRowFragment;